import { useEffect, useState } from 'react';
import CompanyHeader from '@/components/company/CompanyHeader';
import useTenantState from '@/states/tenant.state';
import useOrganizationListState, { IOrganization } from '@/states/organization-list.state';
import useOrgChartState from '@/states/organization.state';
import { Pencil, Trash2, Eye, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';

import SwitchButton from '@/components/company/SwitchButton';

export default function OrganizationList() {
  const { currentTenant } = useTenantState();
  const { organizations, isLoading, fetchOrganizations, createOrganization, updateOrganization, deleteOrganization, setPublished } = useOrganizationListState();
  const { setSelectedOrganizationId } = useOrgChartState();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<IOrganization | null>(null);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<{ name: string; description: string }>();

  useEffect(() => {
    setSelectedOrganizationId(null);
  }, []);

  useEffect(() => {
    if (currentTenant?.id) {
      fetchOrganizations(currentTenant.id);
    }
  }, [currentTenant?.id]);

  useEffect(() => {
    if (!isLoading && organizations.length > 0) {
      navigate(`${organizations[0].id}`, { replace: true });
    }
  }, [isLoading, organizations, navigate]);

  const onSubmit = async (data: { name: string; description: string }) => {
    if (!currentTenant?.id) return;

    if (editingOrg) {
      await updateOrganization(editingOrg.id, data);
    } else {
      // Default to not published when creating
      await createOrganization(currentTenant.id, data);
    }
    closeModal();
  };

  const openAddModal = () => {
    setEditingOrg(null);
    reset({ name: '', description: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (org: IOrganization) => {
    setEditingOrg(org);
    setValue('name', org.name);
    setValue('description', org.description || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingOrg(null);
    reset();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this organization?')) {
      await deleteOrganization(id);
    }
  };

  const handlePublish = async (org: IOrganization, checked: boolean) => {
    if (!currentTenant?.id) return;

    await setPublished(org.id, currentTenant.id, checked);
  };

  return (
    <section className="w-full pb-5">
      <CompanyHeader
        title="Organizations"
        description="Manage your organizations and their structures."
      />

      <div className="mx-5 border-b border-gray-200" />

      <div className="p-5">
        <div className="flex justify-end mb-4">
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
          >
            <Plus size={16} />
                        Add Organization
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-10">Loading organizations...</div>
        ) : (
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="p-4 font-semibold text-gray-600">Name</th>
                  <th className="p-4 font-semibold text-gray-600">Description</th>
                  <th className="p-4 font-semibold text-gray-600 text-center">Status</th>
                  <th className="p-4 font-semibold text-gray-600 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-gray-500">
                                            No organizations found. Create one to get started.
                    </td>
                  </tr>
                ) : (
                  organizations.map((org) => (
                    <tr key={org.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-4 font-medium text-gray-900">{org.name}</td>
                      <td className="p-4 text-gray-600">{org.description || '-'}</td>
                      <td className="p-4 text-center">
                        <div className="flex justify-center flex-col items-center gap-1">
                          <SwitchButton
                            checked={org.is_published}
                            onChange={(checked) => handlePublish(org, checked)}
                          />
                        </div>
                      </td>
                      <td className="p-4 text-right flex justify-end gap-2">
                        <button
                          onClick={() => navigate(`${org.id}`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="View Structure"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => openEditModal(org)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded"
                          title="Edit"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(org.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-xl font-semibold mb-4">
              {editingOrg ? 'Edit Organization' : 'New Organization'}
            </h3>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  {...register('name', { required: 'Name is required' })}
                  className={classNames(
                    "w-full p-2 border rounded focus:ring-primary focus:border-primary",
                    errors.name ? "border-red-500" : "border-gray-300"
                  )}
                />
                {errors.name && <span className="text-red-500 text-sm">{errors.name.message}</span>}
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  {...register('description')}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-primary focus:border-primary"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                >
                                    Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
                >
                                    Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
